import {
  ComponentType,
  KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {AnimatePresence, m} from 'framer-motion';
import Pusher from 'pusher-js';
import clsx from 'clsx';
import {useAuth} from '@common/auth/use-auth';
import {apiClient, queryClient} from '@common/http/query-client';
import {
  BroadcastNotification,
  DatabaseNotification,
  DatabaseNotificationData,
} from '@common/notifications/database-notification';
import {
  getBootstrapData,
  mergeBootstrapData,
} from '@ui/bootstrap-data/bootstrap-data-store';
import {IconButton} from '@ui/buttons/icon-button';
import {Button} from '@ui/buttons/button';
import {Trans} from '@ui/i18n/trans';
import {MixedImage} from '@ui/images/mixed-image';
import {CloseIcon} from '@ui/icons/material/Close';
import {LiveTvIcon} from '@ui/icons/material/LiveTv';
import {PlayArrowIcon} from '@ui/icons/material/PlayArrow';
import {useNavigate} from '@common/ui/navigation/use-navigate';
import {useSettings} from '@ui/settings/use-settings';
import {isAbsoluteUrl} from '@ui/utils/urls/is-absolute-url';
import {SvgIconProps} from '@ui/icons/svg-icon';

const MAX_LIVE_TOASTS = 3;
const FALLBACK_POLL_INTERVAL_MS = 15000;
const POLLING_INITIAL_GRACE_MS = 10000;
const NOTIFICATIONS_QUERY_KEY = ['notifications'];

type LiveToastImage = string | ComponentType<SvgIconProps>;

interface LiveStartedToast {
  id: string;
  artistName: string;
  title: string;
  watchPath: string;
  avatar: LiveToastImage;
  thumbnail: LiveToastImage;
}

interface NotificationsResponse {
  pagination?: {
    data?: DatabaseNotification[];
  };
}

export function LiveStartedToastListener() {
  const {user, isLoggedIn} = useAuth();
  const {realtime} = useSettings();
  const navigate = useNavigate();
  const [toasts, setToasts] = useState<LiveStartedToast[]>([]);
  const visibleToastIds = useRef(new Set<string>());
  const dismissedToastIds = useRef(new Set<string>());
  const knownPolledNotificationIds = useRef(new Set<string>());
  const hasSeededPolling = useRef(false);
  const listenerStartedAt = useRef(Date.now());

  const realtimeEnabled = Boolean(realtime?.enable && realtime?.pusher_key);

  useEffect(() => {
    visibleToastIds.current.clear();
    dismissedToastIds.current.clear();
    knownPolledNotificationIds.current.clear();
    hasSeededPolling.current = false;
    listenerStartedAt.current = Date.now();
    setToasts([]);
  }, [user?.id]);

  const openToast = useCallback(
    (toast: LiveStartedToast) => {
      if (isAbsoluteUrl(toast.watchPath)) {
        window.open(toast.watchPath, '_blank')?.focus();
      } else {
        navigate(toast.watchPath);
      }
    },
    [navigate],
  );

  const dismissToast = useCallback((toastId: string) => {
    dismissedToastIds.current.add(toastId);
    visibleToastIds.current.delete(toastId);
    setToasts(current => current.filter(toast => toast.id !== toastId));
  }, []);

  const addToast = useCallback((toast: LiveStartedToast, incrementUnread = true) => {
    if (
      visibleToastIds.current.has(toast.id) ||
      dismissedToastIds.current.has(toast.id)
    ) {
      return;
    }

    setToasts(current => {
      const nextToasts = [toast, ...current].slice(0, MAX_LIVE_TOASTS);
      visibleToastIds.current = new Set(nextToasts.map(item => item.id));
      return nextToasts;
    });
    queryClient.invalidateQueries({queryKey: NOTIFICATIONS_QUERY_KEY});

    if (incrementUnread) {
      incrementUnreadNotificationsCount();
    }
  }, []);

  const handleBroadcastNotification = useCallback(
    (payload: BroadcastNotification) => {
      const toast = liveToastFromNotification(payload);
      if (toast) {
        addToast(toast);
      }
    },
    [addToast],
  );

  useEffect(() => {
    if (!isLoggedIn || !user?.id || !realtimeEnabled || !realtime?.pusher_key) {
      return;
    }

    const isSecure =
      (realtime.scheme || 'https').includes('https') ||
      (realtime.scheme || '').includes('wss');
    const port = Number(realtime.port) || (isSecure ? 443 : 80);
    const csrfToken = getBootstrapData().csrf_token;
    let pusher: Pusher;

    try {
      pusher = new Pusher(realtime.pusher_key, {
        cluster: realtime.pusher_cluster || 'mt1',
        wsHost: realtime.host || undefined,
        wsPort: port,
        wssPort: port,
        wsPath: normalizeWsPath(realtime.path),
        forceTLS: isSecure,
        enabledTransports: ['ws', 'wss'],
        disableStats: true,
        channelAuthorization: {
          endpoint: internalEndpoint('secure/broadcasting/auth'),
          transport: 'ajax',
          headers: {
            Accept: 'application/json',
            ...(csrfToken ? {'X-CSRF-TOKEN': csrfToken} : {}),
          },
        },
      });
    } catch (error) {
      console.error('Failed to initialize live notification realtime client.', error);
      return;
    }

    const notificationEvent =
      'Illuminate\\Notifications\\Events\\BroadcastNotificationCreated';
    const handleGlobalEvent = (eventName: string, payload: BroadcastNotification) => {
      if (!eventName.includes('BroadcastNotificationCreated')) return;
      handleBroadcastNotification(payload);
    };

    const channelNames = [
      `private-App.Models.User.${user.id}`,
      `private-App.User.${user.id}`,
    ];

    const channels = channelNames.map(channelName => {
      const channel = pusher.subscribe(channelName);
      (channel as any).bind_global(handleGlobalEvent);
      (channel as any).bind(notificationEvent, handleBroadcastNotification);
      (channel as any).bind(`.${notificationEvent}`, handleBroadcastNotification);
      return {channel, channelName};
    });

    return () => {
      channels.forEach(({channel, channelName}) => {
        (channel as any).unbind_global(handleGlobalEvent);
        (channel as any).unbind(notificationEvent, handleBroadcastNotification);
        (channel as any).unbind(
          `.${notificationEvent}`,
          handleBroadcastNotification,
        );
        pusher.unsubscribe(channelName);
      });
      pusher.disconnect();
    };
  }, [
    handleBroadcastNotification,
    isLoggedIn,
    realtime?.enable,
    realtime?.host,
    realtime?.path,
    realtime?.port,
    realtime?.pusher_cluster,
    realtime?.pusher_key,
    realtime?.scheme,
    realtimeEnabled,
    user?.id,
  ]);

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }

    let isActive = true;

    const pollNotifications = async () => {
      try {
        const response = await apiClient.get<NotificationsResponse>('notifications', {
          params: {perPage: 5},
        });
        const notifications = response.data.pagination?.data ?? [];

        if (!hasSeededPolling.current) {
          notifications.forEach(notification => {
            knownPolledNotificationIds.current.add(notification.id);
            const toast = liveToastFromNotification(notification);

            if (
              toast &&
              wasCreatedAfterListenerStarted(
                notification,
                listenerStartedAt.current,
              )
            ) {
              addToast(toast, !notification.read_at);
            }
          });
          hasSeededPolling.current = true;
          return;
        }

        notifications.forEach(notification => {
          if (knownPolledNotificationIds.current.has(notification.id)) {
            return;
          }

          knownPolledNotificationIds.current.add(notification.id);
          const toast = liveToastFromNotification(notification);

          if (toast) {
            addToast(toast, !notification.read_at);
          }
        });
      } catch {
        // Realtime toasts are best-effort. The notifications page remains the source of truth.
      }
    };

    pollNotifications();
    const intervalId = window.setInterval(() => {
      if (isActive) {
        pollNotifications();
      }
    }, FALLBACK_POLL_INTERVAL_MS);

    return () => {
      isActive = false;
      window.clearInterval(intervalId);
    };
  }, [addToast, isLoggedIn]);

  if (!toasts.length) return null;

  return (
    <div className="pointer-events-none fixed inset-x-0 top-24 z-[120] flex justify-center px-12 md:top-32">
      <div className="relative h-[172px] w-full max-w-[620px] md:h-[154px]">
        <AnimatePresence initial={false}>
          {toasts.map((toast, index) => {
            const layer = Math.min(index, MAX_LIVE_TOASTS - 1);
            const topOffset = (MAX_LIVE_TOASTS - 1 - layer) * 18;

            return (
              <m.div
                key={toast.id}
                initial={{opacity: 0, y: -18, scale: 0.98}}
                animate={{
                  opacity: 1 - layer * 0.08,
                  y: 0,
                  scale: 1 - layer * 0.035,
                }}
                exit={{opacity: 0, y: -12, scale: 0.96}}
                transition={{duration: 0.2}}
                style={{
                  zIndex: MAX_LIVE_TOASTS - index,
                  top: topOffset,
                  paddingInline: layer * 14,
                }}
                className="absolute inset-x-0 origin-top"
              >
                <LiveToastCard
                  toast={toast}
                  onOpen={() => openToast(toast)}
                  onDismiss={() => dismissToast(toast.id)}
                />
              </m.div>
            );
          })}
        </AnimatePresence>
      </div>
    </div>
  );
}

interface LiveToastCardProps {
  toast: LiveStartedToast;
  onOpen: () => void;
  onDismiss: () => void;
}

function LiveToastCard({toast, onOpen, onDismiss}: LiveToastCardProps) {
  const avatarIsIcon = toast.avatar === LiveTvIcon;
  const thumbnailIsIcon = toast.thumbnail === LiveTvIcon;

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onOpen();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={handleKeyDown}
      className="pointer-events-auto flex min-h-104 w-full cursor-pointer items-center gap-14 rounded-20 border border-white/15 bg-neutral-950/95 p-12 text-white shadow-[0_22px_70px_rgba(0,0,0,0.45)] outline-none backdrop-blur-xl transition hover:border-primary/60 focus-visible:ring md:gap-16 md:p-14"
    >
      <MixedImage
        src={toast.avatar}
        className={clsx(
          'h-48 w-48 flex-shrink-0 rounded-full',
          avatarIsIcon
            ? 'bg-primary/15 p-10 text-primary'
            : 'bg-neutral-900 object-cover',
        )}
      />
      <div className="min-w-0 flex-1">
        <div className="mb-8 flex items-center gap-8">
          <span className="inline-flex items-center gap-6 rounded-full bg-danger/15 px-8 py-3 text-[11px] font-bold text-danger">
            <span className="h-6 w-6 rounded-full bg-danger" />
            <Trans message="AO VIVO" />
          </span>
        </div>
        <div className="line-clamp-2 text-sm font-semibold leading-5 md:text-base">
          <Trans
            message=":artist está ao vivo: :title"
            values={{artist: toast.artistName, title: toast.title}}
          />
        </div>
        <Button
          variant="flat"
          color="primary"
          size="xs"
          radius="rounded-full"
          className="mt-10"
          startIcon={<PlayArrowIcon />}
          onClick={event => {
            event.stopPropagation();
            onOpen();
          }}
        >
          <Trans message="Watch Live" />
        </Button>
      </div>
      <MixedImage
        src={toast.thumbnail}
        className={clsx(
          'hidden h-60 w-106 flex-shrink-0 rounded object-cover sm:block',
          thumbnailIsIcon && 'bg-neutral-900 p-18 text-primary',
        )}
      />
      <IconButton
        aria-label="Close"
        className="self-start text-white/80 hover:bg-white/10 hover:text-white"
        size="sm"
        onClick={event => {
          event.stopPropagation();
          onDismiss();
        }}
      >
        <CloseIcon />
      </IconButton>
    </div>
  );
}

function liveToastFromNotification(
  notification: BroadcastNotification | DatabaseNotification,
): LiveStartedToast | null {
  const data = notificationData(notification);
  const meta = (data.meta ?? {}) as Record<string, unknown>;

  if (meta.toast_type !== 'live_started') return null;

  const watchPath = stringValue(meta.watch_path) || data.mainAction?.action;
  if (!watchPath) return null;

  const artistName = stringValue(meta.artist_name) || 'Live';
  const title = stringValue(meta.title) || plainTextFromLines(data) || 'Live';
  const id = liveToastId(notification, meta);

  return {
    id,
    artistName,
    title,
    watchPath,
    avatar: data.image && data.image !== 'media-notification' ? data.image : LiveTvIcon,
    thumbnail: data.thumbnail || LiveTvIcon,
  };
}

function notificationData(
  notification: BroadcastNotification | DatabaseNotification,
): DatabaseNotificationData {
  return 'data' in notification ? notification.data : notification;
}

function liveToastId(
  notification: BroadcastNotification | DatabaseNotification,
  meta: Record<string, unknown>,
) {
  return [
    'live-started',
    stringValue(meta.artist_live_session_id) ||
      stringValue(meta.artist_live_schedule_id) ||
      notification.id,
  ].join(':');
}

function stringValue(value: unknown): string | undefined {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return `${value}`;
  return undefined;
}

function plainTextFromLines(data: DatabaseNotificationData): string | undefined {
  const content = data.lines?.[0]?.content;
  if (!content) return undefined;

  return content
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'");
}

function incrementUnreadNotificationsCount() {
  const bootstrapData = getBootstrapData();
  if (!bootstrapData.user) return;

  mergeBootstrapData({
    user: {
      ...bootstrapData.user,
      unread_notifications_count:
        (bootstrapData.user.unread_notifications_count ?? 0) + 1,
    },
  });
}

function wasCreatedAfterListenerStarted(
  notification: DatabaseNotification,
  listenerStartedAt: number,
) {
  const createdAt = new Date(notification.created_at).getTime();

  if (!Number.isFinite(createdAt)) return false;

  return createdAt >= listenerStartedAt - POLLING_INITIAL_GRACE_MS;
}

function internalEndpoint(path: string) {
  const base = getBootstrapData().settings.html_base_uri || '';
  const normalizedBase = base === '/' ? '' : base.replace(/\/+$/g, '');
  return `${normalizedBase}/${path.replace(/^\/+/g, '')}`;
}

function normalizeWsPath(path?: string | null) {
  const normalized = (path || '').trim();

  if (!normalized) return '';

  const withoutSlashes = normalized.replace(/^\/+|\/+$/g, '');
  if (!withoutSlashes || withoutSlashes === 'app') {
    return '';
  }

  return `/${withoutSlashes}`;
}
